import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, SpinnerIcon, FireIcon } from '@phosphor-icons/react';
import { TenorService } from '../../lib/chatService';

interface GifPickerProps {
    onSelect: (gifUrl: string) => void;
    onClose: () => void;
}

interface GifItem {
    id: string;
    url: string;
    preview: string;
    width: number;
    height: number;
}

export const GifPicker: React.FC<GifPickerProps> = ({ onSelect, onClose }) => {
    const [search, setSearch] = useState('');
    const [gifs, setGifs] = useState<GifItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState<'trending' | 'search'>('trending');
    const pickerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Load trending GIFs on mount
    useEffect(() => {
        const loadTrending = async () => {
            setLoading(true);
            const results = await TenorService.getTrendingGifs(24);
            setGifs(results);
            setLoading(false);
        };
        loadTrending();
    }, []);

    // Search with debounce
    const handleSearch = (query: string) => {
        setSearch(query);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!query.trim()) {
            setMode('trending');
            TenorService.getTrendingGifs(24).then(setGifs);
            return;
        }

        setMode('search');
        setLoading(true);

        searchTimeoutRef.current = setTimeout(async () => {
            const results = await TenorService.searchGifs(query, 24);
            setGifs(results);
            setLoading(false);
        }, 300);
    };

    return (
        <div
            ref={pickerRef}
            className="absolute bottom-full left-0 mb-2 w-80 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden z-50"
        >
            {/* Header */}
            <div className="p-2 border-b border-zinc-800">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search Tenor..."
                        className="w-full pl-8 pr-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        autoFocus
                    />
                </div>
            </div>

            {/* Mode Indicator */}
            <div className="px-3 py-1.5 text-xs text-zinc-500 border-b border-zinc-800 flex items-center gap-1.5">
                {mode === 'trending' ? (
                    <>
                        <FireIcon className="w-3.5 h-3.5 text-orange-400" />
                        <span>Trending</span>
                    </>
                ) : (
                    <span>Results for "{search}"</span>
                )}
            </div>

            {/* GIF Grid */}
            <div className="p-2 max-h-64 overflow-y-auto custom-scrollbar">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <SpinnerIcon className="w-6 h-6 text-zinc-500 animate-spin" />
                    </div>
                ) : gifs.length === 0 ? (
                    <p className="text-center text-zinc-500 text-sm py-8">
                        {mode === 'search' ? 'No GIFs found' : 'Loading...'}
                    </p>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        {gifs.map(gif => (
                            <button
                                key={gif.id}
                                onClick={() => onSelect(gif.url)}
                                className="relative group overflow-hidden rounded-lg bg-zinc-800 hover:ring-2 hover:ring-indigo-500 transition-all"
                                style={{ paddingBottom: `${(gif.height / gif.width) * 100}%` }}
                            >
                                <img
                                    src={gif.preview}
                                    alt="GIF"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    loading="lazy"
                                />
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Tenor Attribution */}
            <div className="px-3 py-1.5 border-t border-zinc-800 flex items-center justify-center">
                <span className="text-[10px] text-zinc-600">Powered by Tenor</span>
            </div>
        </div>
    );
};

export default GifPicker;
